import { Direction } from '../types'

/** True when a keystroke is destined for a text field (input/textarea/select/contenteditable).
 * The game's global key handler must NOT hijack or preventDefault these, or the user can't type
 * movement keys (WASD/arrows) into fields like the leaderboard nickname. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el || typeof el.tagName !== 'string') return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true
}

/** Unified input: keyboard + gamepad + touch-swipe + on-screen d-pad → a single
 * persistent "desired direction" (Pac-Man holds the last requested heading) plus
 * edge-triggered action flags. */
export class Input {
  desired: Direction = Direction.None
  private pausePressed = false
  private confirmPressed = false
  private anyPressed = false

  private keyToDir: Record<string, Direction> = {
    ArrowUp: Direction.Up,
    KeyW: Direction.Up,
    ArrowDown: Direction.Down,
    KeyS: Direction.Down,
    ArrowLeft: Direction.Left,
    KeyA: Direction.Left,
    ArrowRight: Direction.Right,
    KeyD: Direction.Right,
  }

  private touchStart: { x: number; y: number } | null = null
  private readonly swipeThreshold = 24
  private prevPad = { pause: false, confirm: false }
  private el: HTMLElement | null = null

  attach(el: HTMLElement): void {
    this.el = el
    window.addEventListener('keydown', this.onKeyDown)
    el.addEventListener('touchstart', this.onTouchStart, { passive: false })
    el.addEventListener('touchmove', this.onTouchMove, { passive: false })
    el.addEventListener('touchend', this.onTouchEnd, { passive: false })
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown)
    if (this.el) {
      this.el.removeEventListener('touchstart', this.onTouchStart)
      this.el.removeEventListener('touchmove', this.onTouchMove)
      this.el.removeEventListener('touchend', this.onTouchEnd)
    }
  }

  /** Public setter for on-screen d-pad buttons (mobile HUD). */
  setDesired(dir: Direction): void {
    this.desired = dir
    this.anyPressed = true
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Never consume keystrokes meant for a focused text field (e.g. the nickname input),
    // otherwise WASD/arrows get preventDefault'd and can't be typed.
    if (isEditableTarget(e.target)) return
    const dir = this.keyToDir[e.code]
    if (dir) {
      this.desired = dir
      this.anyPressed = true
      e.preventDefault()
    } else if (e.code === 'Escape' || e.code === 'KeyP') {
      this.pausePressed = true
    } else if (e.code === 'Enter' || e.code === 'Space') {
      this.confirmPressed = true
      this.anyPressed = true
    }
  }

  private onTouchStart = (e: TouchEvent): void => {
    const t = e.changedTouches[0]
    this.touchStart = { x: t.clientX, y: t.clientY }
  }

  private onTouchMove = (e: TouchEvent): void => {
    // Prevent scroll/zoom while interacting with the canvas.
    if (this.touchStart) e.preventDefault()
  }

  private onTouchEnd = (e: TouchEvent): void => {
    if (!this.touchStart) return
    const t = e.changedTouches[0]
    const dx = t.clientX - this.touchStart.x
    const dy = t.clientY - this.touchStart.y
    this.touchStart = null
    if (Math.abs(dx) < this.swipeThreshold && Math.abs(dy) < this.swipeThreshold) {
      this.confirmPressed = true
      return
    }
    if (Math.abs(dx) > Math.abs(dy)) {
      this.desired = dx > 0 ? Direction.Right : Direction.Left
    } else {
      this.desired = dy > 0 ? Direction.Down : Direction.Up
    }
    this.anyPressed = true
  }

  /** Poll connected gamepad each frame. */
  private pollGamepad(): void {
    const pads = navigator.getGamepads?.() ?? []
    for (const pad of pads) {
      if (!pad) continue
      const ax = pad.axes[0] ?? 0
      const ay = pad.axes[1] ?? 0
      const dead = 0.4
      const up = pad.buttons[12]?.pressed || ay < -dead
      const down = pad.buttons[13]?.pressed || ay > dead
      const left = pad.buttons[14]?.pressed || ax < -dead
      const right = pad.buttons[15]?.pressed || ax > dead
      if (up) this.setDesired(Direction.Up)
      else if (down) this.setDesired(Direction.Down)
      else if (left) this.setDesired(Direction.Left)
      else if (right) this.setDesired(Direction.Right)

      const pausePad = pad.buttons[9]?.pressed ?? false // Start
      const confirmPad = pad.buttons[0]?.pressed ?? false // A
      if (pausePad && !this.prevPad.pause) this.pausePressed = true
      if (confirmPad && !this.prevPad.confirm) this.confirmPressed = true
      this.prevPad.pause = pausePad
      this.prevPad.confirm = confirmPad
      return
    }
  }

  update(): void {
    this.pollGamepad()
  }

  consumePause(): boolean {
    const v = this.pausePressed
    this.pausePressed = false
    return v
  }

  consumeConfirm(): boolean {
    const v = this.confirmPressed
    this.confirmPressed = false
    return v
  }

  consumeAnyPress(): boolean {
    const v = this.anyPressed
    this.anyPressed = false
    return v
  }

  reset(): void {
    this.desired = Direction.None
    this.pausePressed = false
    this.confirmPressed = false
    this.anyPressed = false
    this.touchStart = null
  }
}
